import { NavLink, useNavigate } from 'react-router';
import authStore from '@store/auth-store.ts';
import { observer } from 'mobx-react-lite';
import Icon from '@components/core/icon/icon.tsx';
import { IconEnum } from '@components/core/icon/icon-enum.ts';
import ExpansionButton from '@components/core/expansion-button/expansion-button.tsx';
import viewStore from '@store/view-store.ts';
import navSidebarStore from '@store/nav-sidebar-store.ts';
import ClassString from '@utils/class-string.ts';
import ThemeSelector from '@components/core/theme-selector/theme-selector.tsx';
import useScreenBreakpoint from '@hooks/use-screen-breakpoint.tsx';
import { ScreenBreakpoint } from '@/enums/screen-breakpoints.ts';
import GlobalExperienceSearch from '@components/core/global-experience-search/global-experience-search.tsx';
import NavigationButton from '@components/navigation/navigation-button/navigation-button.tsx';
import { UserRole } from '@/schemas/user-schema.ts';
import CapitalizeWords from '@utils/capitalize-words.ts';
import storeService from '@services/store-service.ts';

const Nav = observer(function Nav() {
	const navigate = useNavigate();
	const screenBreakpoint = useScreenBreakpoint();

	const sessionRestored = authStore.sessionRestored;
	const loggedIn = authStore.isLoggedIn;

	const hideSidebar = navSidebarStore.hide();

	const iconRoute = viewStore.viewInfo?.breadCrumbs
		? viewStore.viewInfo?.breadCrumbs[0].route
		: undefined;

	const hideFirstBreadcrumb =
		!!viewStore.viewInfo?.breadCrumbs &&
		viewStore.viewInfo.breadCrumbs.length > 0 &&
		screenBreakpoint <= ScreenBreakpoint.SM;

	const showBreadcrumb = (index: number) => {
		return (hideFirstBreadcrumb && index > 0) || !hideFirstBreadcrumb;
	};

	return (
		<div
			className={ClassString({
				static: 'fixed top-0 z-nav w-screen drop-shadow-sm bg-white dark:bg-gray-600 right-0',
				dynamic: {
					'lg:w-nav': !hideSidebar,
					'lg:w-minimal-nav': !hideSidebar && navSidebarStore.minimal,
				},
			})}
		>
			<div
				className={ClassString({
					static: 'h-nav flex justify-center items-center px-4 lg:px-6 visible w-full',
					dynamic: {
						'h-0 overflow-hidden': hideSidebar,
					},
				})}
				style={{
					transition:
						'height 400ms cubic-bezier(0.165, 0.84, 0.44, 1)',
				}}
			>
				<div
					className={
						'flex flex-1 items-center whitespace-nowrap truncate'
					}
				>
					{viewStore.viewInfo && (
						<div className={'flex items-center w-full truncate'}>
							{viewStore.viewInfo.icon && (
								<>
									<div
										className={
											'mr-2 flex justify-center items-center'
										}
									>
										{!!iconRoute && (
											<NavLink
												to={iconRoute}
												draggable="false"
											>
												<Icon
													icon={
														viewStore.viewInfo.icon
													}
													size={
														viewStore.viewInfo
															.iconSize ??
														'2.5rem'
													}
												/>
											</NavLink>
										)}

										{!iconRoute && (
											<Icon
												icon={viewStore.viewInfo.icon}
												size={
													viewStore.viewInfo
														.iconSize ?? '2.5rem'
												}
											/>
										)}
									</div>
								</>
							)}
							<div
								className={
									'flex justify-center items-center lg:text-xl truncate'
								}
							>
								{viewStore.viewInfo.breadCrumbs?.map(
									(breadcrumb, index) => {
										return (
											<div
												className={
													'flex justify-center items-center'
												}
												key={`nav-bc-${index}`}
											>
												{showBreadcrumb(index) && (
													<>
														{!!breadcrumb.route && (
															<NavLink
																to={
																	breadcrumb.route
																}
																draggable="false"
																className={
																	'mr-2 lg:mr-3 no-underline text-inherit'
																}
															>
																{
																	breadcrumb.title
																}
															</NavLink>
														)}
														{!breadcrumb.route && (
															<div
																className={
																	'lg:mr-3'
																}
															>
																{
																	breadcrumb.title
																}
															</div>
														)}
													</>
												)}
												<div
													className={
														'mr-2 lg:mr-3 mt-0.5 flex justify-center items-center'
													}
												>
													<Icon
														icon={
															IconEnum.BREADCRUMB_ARROW
														}
														size={'1.2rem'}
													/>
												</div>
											</div>
										);
									}
								)}

								<div className={'truncate'}>
									{viewStore.viewInfo.title}
								</div>
							</div>
						</div>
					)}
				</div>

				<div className={'flex justify-end items-center select-none'}>
					{sessionRestored && (
						<>
							<ThemeSelector className={'mr-6 hidden lg:flex'} />

							{loggedIn && (
								<ExpansionButton
									className={'hidden lg:flex'}
									title={`${authStore.user?.first_name ?? ''} ${
										authStore.user?.last_name ?? ''
									}`}
									icon={{
										icon: IconEnum.ACCOUNT,
										placement: 'right',
										size: '28px',
									}}
									subMenuButtons={[
										...(authStore.canSimulateRole
											? [
													{
														title: authStore.roleSimulated
															? CapitalizeWords(
																	`Simulated Role: ${authStore.user?.role}`
																)
															: 'Simulate Role',
														onClick: () => {
															if (
																!authStore.user
															) {
																return;
															}

															switch (
																authStore.user
																	?.role
															) {
																case UserRole.ADMIN:
																	authStore.simulateRole(
																		UserRole.EDITOR
																	);
																	break;
																case UserRole.EDITOR:
																	authStore.simulateRole(
																		UserRole.VIEWER
																	);
																	break;
																case UserRole.VIEWER:
																	authStore.simulateRole(
																		UserRole.ADMIN
																	);
																	break;
															}
														},
													},
												]
											: []),
										{
											title: 'Settings',
											onClick: () => {
												void navigate('/settings');
											},
										},
										{
											title: 'Sign Out',
											onClick: async () => {
												storeService.resetDataOnSignOut();
												await authStore.logOut();
												void navigate('/overview');
											},
										},
									]}
								/>
							)}

							{!loggedIn && (
								<div
									className={
										'flex justify-center items-center gap-4'
									}
								>
									<NavigationButton
										path={'/sign-up'}
										title={'Sign Up for Free'}
										outline
										className={
											'hidden lg:flex bg-red-200 border-gray-400 dark:border-gray-500 dark:text-gray-600'
										}
									/>

									<NavigationButton
										path={'/sign-in'}
										title={'Sign In'}
										outline
										className={'hidden lg:flex'}
									/>
								</div>
							)}
						</>
					)}

					<div className={'lg:hidden'}>
						<GlobalExperienceSearch hideShortcut />
					</div>

					<Icon
						icon={
							navSidebarStore.open
								? IconEnum.CLEAR
								: IconEnum.MENU
						}
						className={
							'p-1 flex lg:hidden text-5xl ml-1 rounded-full cursor-pointer hover:bg-blue-300 hover:text-gray-600'
						}
						onClick={() => navSidebarStore.toggleOpen()}
						size={'2.8rem'}
					/>
				</div>
			</div>
		</div>
	);
});

export default Nav;
